import { formatNumToCompactNotation } from '@modules/common/utils';
import { trackDashboardEvent } from '@modules/dashboard/helpers';
import { useGetDashboardAnalysisLinkWithState } from '@modules/dashboard/hooks';
import { useUpdateFeedQueryStateForGlobalFilters } from '@modules/dashboard/hooks/useUpdateFeedQueryStateForGlobalFilters';
import { useGetFeedDuration, useGetFeedWhereClause } from '@modules/feed/hooks';
import { FeedbackSummaryGlobalToggle } from '@modules/feedback/components/FeedbackList/FeedbackSummaryGlobalToggle';
import { SHOULD_SHOW_FEEDBACK_LIST_SUMMARY_VIEW } from '@modules/feedback/constants';
import { useGetSelectedFeedbackMetadata } from '@modules/feedback/hooks';
import { useGetFeedbackList } from '@modules/feedback/hooks/useGetFeedbackList';
import { FeedbackListStoreProvider } from '@modules/feedback/store/useFeedbackListStore';
import { FeedbackListContainer } from '@modules/feedbackV2/common/components/FeedbackListContainer';
import { ArtefactCitationFeedbackList } from '@modules/oracle/components/ArtefactCitationFeedbackList';
import { WisdomIcon } from '@modules/oracle/components/WisdomIcon';
import { Box, LinearProgress, Stack, Typography, alpha } from '@mui/material';
import { AnalysisViewFragment, TimeUnit } from 'generated/graphql';
import Locker from 'lockr';
import { useMemo, useState } from 'react';
import { useMeasure, useToggle } from 'react-use';
import { TimeRange } from 'v2/core/entities';

interface DashboardFeedListProps {
  analysis: AnalysisViewFragment;
}

export function DashboardFeedList({ analysis }: DashboardFeedListProps) {
  const duration = useGetFeedDuration();
  const whereClause = useGetFeedWhereClause();
  const [totalRecordCount, setTotalRecordCount] = useState<
    number | undefined
  >();
  const [bannerRef, { height: bannerHeight }] = useMeasure<HTMLDivElement>();
  const [isSummaryView, setIsSummaryView] = useState<boolean>(
    Locker.get(SHOULD_SHOW_FEEDBACK_LIST_SUMMARY_VIEW) ?? false,
  );
  const [shouldOpenFeedbackDrawer, toggleShouldOpenFeedbackDrawer] =
    useToggle(false);

  const queryContainer = useMemo(
    () => ({
      AggregatePeriod: TimeUnit.Week,
      DateRange: duration as TimeRange,
      WhereClause: whereClause,
    }),
    [whereClause, duration],
  );

  useUpdateFeedQueryStateForGlobalFilters();

  const getFeedbackListResp = useGetFeedbackList({
    queryContainer,
    shouldSkip: true, // no need to call this, API integration will be different for feedback redesign
  });

  const { analysisLink } = useGetDashboardAnalysisLinkWithState(analysis);

  const selectedFeedbackMetadata = useGetSelectedFeedbackMetadata({
    whereClause: queryContainer?.WhereClause,
  });

  const handleSummaryViewChange = (isSummaryView: boolean) => {
    setIsSummaryView(isSummaryView);
  };

  const showFeedbackRecords = () => {
    toggleShouldOpenFeedbackDrawer();
    trackDashboardEvent({
      eventName: 'Dashboard Feed List - See All Records Clicked',
      analysisLink: analysisLink,
      dashboardLink: window.location.href,
    });
  };

  const renderBanner = () => {
    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{
          py: 0.5,
          px: 2,
          backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.05),
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
        ref={bannerRef}
      >
        <Stack
          direction="row"
          alignItems="center"
          minWidth={0}
          spacing={1}
          sx={{ cursor: 'pointer' }}
          onClick={showFeedbackRecords}
        >
          <WisdomIcon
            color="primary"
            sx={{
              fontSize: '0.8rem',
            }}
          />
          <Typography
            component={Stack}
            direction="row"
            alignItems="center"
            variant="caption"
            color="primary"
            sx={{
              overflowX: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: '1rem',
              overflowY: 'auto',
              maxHeight: '50px',
            }}
          >
            Ask/Summarise{' '}
            {totalRecordCount === undefined ? (
              <LinearProgress sx={{ width: 30, mx: 1 }} />
            ) : (
              formatNumToCompactNotation({ value: totalRecordCount })
            )}{' '}
            Feedback Records
          </Typography>
        </Stack>
        <FeedbackSummaryGlobalToggle
          isSummaryView={isSummaryView}
          onChange={handleSummaryViewChange}
          showShrinkedButton={true}
          shouldScaleButton={false}
          toggleLocation="Search List"
          height="32px"
          containerSx={{
            position: 'static',
            m: 0,
          }}
        />
      </Stack>
    );
  };

  const renderContent = () => {
    return (
      <Stack
        direction="column"
        sx={{ overflowY: 'auto', height: `calc(100% - ${bannerHeight}px)` }}
      >
        <FeedbackListStoreProvider>
          <Box sx={{ mt: '-16px' }}>
            <FeedbackListContainer
              selectedFeedbackMetadata={selectedFeedbackMetadata}
              queryContainer={queryContainer}
              isSummaryView={isSummaryView}
              {...getFeedbackListResp}
              onFirstPageFetch={(resp) => setTotalRecordCount(resp.hits)}
              shouldShowScrollToTopButton={false}
            />
          </Box>
        </FeedbackListStoreProvider>
      </Stack>
    );
  };

  return (
    <Box sx={{ height: '100%' }}>
      {renderBanner()}
      {renderContent()}
      {queryContainer && (
        <ArtefactCitationFeedbackList
          shouldShowFeedbackDrawer={shouldOpenFeedbackDrawer}
          closeFeedbackDrawer={toggleShouldOpenFeedbackDrawer}
          queryContainer={queryContainer}
          analysis={{
            ...analysis,
            panels: [],
            reporting: {},
          }}
        />
      )}
    </Box>
  );
}
